import React from 'react';
import {LinkComponent, LinkComponentProps, useLocalizationLocale} from 'epic-games-nav-shared';
import invariant from 'tiny-invariant';
import {useServerURL} from './EmbeddedProvider';

export const EmbeddedNavLinkComponent: LinkComponent = ({
  path,
  externalHrefTemplate,
  explicitLocale,
  isEmbedded,
  locale,
  children,
  className,
  ...props
}: LinkComponentProps) => {
  invariant(isEmbedded, 'EmbeddedNavLinkComponent is only to be used on embedded sites');
  const serverURL = useServerURL();

  const activeLocale: string = useLocalizationLocale();
  const isLocaleSwitch: boolean = activeLocale !== locale;
  const clientUrl: URL = new URL(window.location.href);

  // URL without search params
  const targetHref: string = new URL(path, serverURL.origin).href;

  // Can be either a fully qualified URL (e.g. www.epicgames.com/path/to/visit)
  // or an absolute path (e.g. /path/to/visit) in which case we assume the domain
  // where the nav is loaded from - not the domain where the nav is shown on
  if (externalHrefTemplate) {
    const externalHrefWithLocale = externalHrefTemplate.replace('{locale}', locale);
    const targetHref = externalHrefTemplate.startsWith('/')
      ? `${serverURL.origin}${externalHrefWithLocale}`
      : externalHrefWithLocale;
    return (
      <a href={targetHref} className={className} {...props}>
        {children}
      </a>
    );
  }

  // For now this ignores any scenario's where the locale of a link would be
  // different other than the language switch menu, in which case we want to
  // refer to the origin of the client application.

  // traditionally epic has two ways of providing the locale (that can be used
  // simultaneously), either in the URL path or as a query parameter. The latter
  // takes priority and is normally used to updatte the cookie value, so that is
  // what we pass here.
  if (isLocaleSwitch) {
    return (
      <a href={`${clientUrl.origin}${path}?lang=${locale}`} className={className} {...props}>
        {children}
      </a>
    );
  }

  return (
    <a
      href={clientUrl.searchParams.get('lang') ? `${targetHref}${clientUrl.search}` : targetHref}
      className={className}
      {...props}>
      {children}
    </a>
  );
};
